import _ from "underscore";
import rison from "rison";
import DriveView from "../../../../DriveView";
import { handleJSNative, handleApi } from "../../../../utils/error";
import PageError from "../../../../utils/pageError";
import { isSleeperBerthDisabled } from "../../../../utils/device";
import { BaseCache, makeDeviceCache, makeUserCache, makeTrailerAttachmentCache } from "../../../../utils/storage/cache";
import DutyStatusLog, {
    IAnnotation,
    NOUSERID
} from "../../../../model/DutyStatusLog";
import { INewAnnotation } from "../../../../model/DutyStatusLogs";
import { Device } from "../../../../model/Device";
import { TrailerAttachments } from "../../../../model/TrailerAttachments";
import { dstCalculator, getStartOfDayUserTimezone } from "../../../../utils/date";
import Api from "../../../../services/api";
import Sync from "../../../../sync/sync";
import SelectDialog from "../../../framework/selectDialog";
import DateTimeSelect from "../../../framework/dateTimeSelect";
import DurationSelect from "../../../framework/durationSelect";
import { convertMinutesToTimeString, setUsingReassignApi } from "../../../../utils/helpers";
import { Dialog } from "../../../framework/dialog";
import { CancelablePromise as Promise } from "../../../../utils/cancelablePromise";
import Validator, { ValidatorParameters } from "../../../../utils/validator";
import idSerializer from "geotab-uuid";
import { getServerDate } from "../../../../utils/storage/date";
import { populateAddressesRedux } from "../../../../sync/common";
import dayjs from "../../../../utils/dayjs";
import HosProcess from "../../../../services/hosProcess";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { extend, find, hide, show } from "../../../../utils/vanilla";
import AutoCompletionInput from "../../../framework/autoCompletionInput";
import { PageLevel } from "../../../../store/mobile/types";
import toastr, { ToastOrigins } from "../../../../utils/toastr";
import { MobileActions } from "../../../../store/mobile/actions";
import constants from "../../../../constants";
import { getCustomExemptHOSAbbreviation, getTranslatedStatus, shouldBlockEditingDLog } from "../../../../utils/hosHelpers";
import pageHTML from "./view.tmpl";
import unassignDialogHtml from "./unassignDialog.tmpl";
import availabilityPreviewDialog from "../../../framework/availabilityDisplay/availabilityPreviewDialog.tmpl";
import { type IDutyStatusLog, type IHosViolationSlim, MINUTES, type THosStatus, populateViolationReason,
    roundBasedOnStatus, setCoDriversForCarrierDriverRequest } from "hos-tools";
import logDurationCalculator from "../../../../utils/logDurationCalculator";
import ToggleSwitch from "../../../framework/toggleSwitch";
import { HosAddress } from "../../../framework/hosAddress/hosAddress";
import AvailabilityDisplay from "../../../framework/availabilityDisplay/availabilityDisplay";
import { getClonedAnnotationsForUser } from "../../../../utils/storeHelpers";
import { TrailerAttachment } from "../../../../model/TrailerAttachment";
import ShortenOrRemoveLogDialog from "../../../framework/shortenOrRemoveLogDialog";
import { EditPcYmAction, ShortenYmPcError, validateExemptionTimeModification, IReviewChangesParams } from "./editPcOrYmUtils";
import { formatExemptionEditAnnotation, getLinkedExemptionAnnotations, indexLinkedExemptionAnnotations } from "./exemptionEditAnnotations";
import { SyncType } from "../../../../sync/sync.types";
import { HistoricalDayActions } from "../../../../store/hosLogs/actions";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";

const VIOLATION_HOURS_REGEX = /\d+/g;

export default class ViewHosView extends DriveView<any> {
    model: DutyStatusLog;

    protected strings = [
        "Saving in progress",
        "View log",
        "Saved",
        "{0} days",
        "h",
        "{minutes}m",
        "Yes",
        "No",
        "{0} Break duration",
        "{0} Break",
        "{0} Break required in",
        "Not available",
        "Accept",
        "Reject",
        "OFF",
        "SB",
        "D",
        "ON",
        "PC",
        "YM",
        "WT",
        "Edits saved",
        "Edits accepted"
    ];
    private readonly template = _.template(pageHTML);
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private readonly userCache: BaseCache<IUser>;
    private readonly trailerAttachmentCache: BaseCache<TrailerAttachment>;
    private readonly initialCollection: DutyStatusLog[];
    private readonly currentUser: DeepReadonly<IUser> | null;
    private readonly violations: IHosViolationSlim[];
    private readonly hosUtils: HosProcess;
    private readonly initialNextLog: DutyStatusLog | null;
    private readonly validator: Validator;
    private readonly availabilityDisplay: AvailabilityDisplay;
    private readonly workflow: boolean;
    private readonly fromMain: boolean;
    private readonly isEwd: boolean;
    private readonly isEmergencyResponseExemptionEnabled: boolean;
    private readonly canIgnoreLog: boolean;
    private readonly toggleSwitch: ToggleSwitch;
    private readonly clickLogAction: (reviewLogChangesParams: IReviewChangesParams) => void;
    private errors: PageError[] = [];
    private modelChanged = false;
    private needsConfirmIgnore = false;
    private annotationChanged = false;
    private logCollection: DutyStatusLog[];
    private logFromStorage: DutyStatusLog;
    private annotationInput?: AutoCompletionInput;
    private scrollToBottom: boolean;
    private cancelRedirect = false;
    private dateTimeErrorLog: DutyStatusLog | null = null;
    private duration?: number | null;

    private readonly trailerAttachmentCollection: TrailerAttachments;
    private readonly hosAddress: HosAddress;
    private readonly canModifyPcYmPeriod: boolean = false;
    private readonly requestedExemptionRelatedLog: DutyStatusLog | null = null;
    private readonly exemptionStartLog: DutyStatusLog | null = null;
    private readonly exemptionEndLog: DutyStatusLog | null = null;

    /*
     * Initialize the view, set up a new HOS log report, precompile the template
     */
    constructor(options) {
        super(options, {
            title: "HOS Log",
            activeSection: "hos",
            pageLevel: options.pageLevel || PageLevel.TWO
        });
        this.validator = new Validator(this.app.dutyStatusLogService);
        this.availabilityDisplay = new AvailabilityDisplay();
        this.hosUtils = this.app.hosUtils;
        this.sync = this.app.sync;
        this.api = this.app.api;
        this.trailerAttachmentCollection = new TrailerAttachments(this.api, this.app.store);
        let currentId: string = options.state.id;
        this.clickLogAction = options && options.clickLogAction;
        this.currentUser = this.app.userService.getCurrentUser();
        this.deviceCache = makeDeviceCache(this.app.store);
        this.userCache = makeUserCache(this.app.store);
        this.trailerAttachmentCache = makeTrailerAttachmentCache(this.app.store);
        this.isEmergencyResponseExemptionEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_EMERGENCY_RESPONSE_EXEMPTION);
        this.fetchCurrentLog(currentId);
        this.isEwd = this.model && this.model.isEwdLog();

        // needGoBack() covers the no-model case, so it has to run before anything below
        // dereferences this.model. fetchCurrentLog leaves the model null whenever the requested log
        // is in neither logCollection nor the historical day cache — most commonly when a
        // continuation placeholder is opened and its parent log's day was never loaded (a date-picker
        // jump) or has since been evicted from the historical cache.
        if (this.needGoBack()) {
            this.goBack();
        } else {
            if (this.model.isEditableExemptionLog()) {
                const allStatusLogs = this.app.dutyStatusLogService.getAllHOSStatusLogsIncludingSDC(true, undefined, false);
                const exemptionLogIndex = this.app.dutyStatusLogService.indexOf(allStatusLogs, this.model);
                this.exemptionStartLog = this.app.dutyStatusLogService.getFirstExemptionLogInPeriod(this.model, exemptionLogIndex, allStatusLogs);
                this.exemptionEndLog = this.app.dutyStatusLogService.getLastLogInExemptionPeriod(this.model, exemptionLogIndex, allStatusLogs);
                this.requestedExemptionRelatedLog = this.app.dutyStatusLogService.getRelatedRequestedLogIfExists(this.exemptionStartLog, undefined, allStatusLogs);
                this.canModifyPcYmPeriod = !this.requestedExemptionRelatedLog && this.app.securityService.can("editPcYmLogs");
            }

            this.initialNextLog = this.app.dutyStatusLogService.getNormalNext(this.logCollection, this.model);
            if (this.initialNextLog) {
                this.initialNextLog = new DutyStatusLog(this.initialNextLog);
            }
            this.canIgnoreLog = this.app.securityService.can("canIgnoreHOSLogs", this.currentUser?.id) && this.model.isIgnorableLog() && !this.requestedExemptionRelatedLog;
            this.toggleSwitch = new ToggleSwitch({
                labelId: "ignoreLog-toggle-label",
                switchId: "ignoreLog-toggle",
                onText: "Yes",
                offText: "No",
                ariaLabel: "Ignore log toggle",
                isEnabled: !!this.model.isIgnored
            });

            this.trailerAttachmentCollection.add(this.trailerAttachmentCache.get());
            this.violations = options.state.violations;
            this.workflow = options.workflow;
            this.fromMain = options.state.fromMain || false;
            this.scrollToBottom = options.state.fromMain || false;
        }

        this.initialCollection = this.app.dutyStatusLogService.clone(this.logCollection);
        this.hosAddress = new HosAddress(this.translator, this.sync);
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #editStatus": this.editStatus,
            "click #editDate": this.editDate,
            "click #editDuration": this.editDuration,
            "click #saveDutyStatusLog": this.saveLog,
            "click #acceptRequest": this.acceptRequest,
            "click #rejectRequest": this.rejectRequest,
            "click #unassignLog": this.unassignLog,
            "click #view-existing-log": this.viewExistingLog,
            "change #ignoreLog-toggle": this.toggleIgnoreLog,
            "click .addressButton": this.manualInputAddress,
            "click #editExemption": this.editExemption,
            "click #previewRequestedExemptionChanges": this.previewRequestedExemptionChanges
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */
    onBeforeUnload = () => {
        return new Promise((resolve, reject) => {
            let leave = (dialog: Dialog) => {
                this.modelChanged = false;
                this.annotationChanged = false;
                resolve();
                dialog.remove();
            };
            let leaveConfirmDialog = new Dialog(
                null,
                "You have unsaved changes. Would you like to save your changes before continuing?",
                this.translator,
                "Save changes?",
                [
                    [
                        {
                            id: "confirmSave",
                            text: "Save",
                            callback: dialog => {
                                leaveConfirmDialog.waiting.start();
                                this.save()
                                    .then(leave.bind(this, dialog))
                                    .catch(error => {
                                        this.handleErrors(error);
                                        leaveConfirmDialog.remove();
                                    });
                            }
                        },
                        {
                            id: "confirmLeave",
                            text: "Don't save",
                            className: "button_secondary",
                            callback: leave
                        }
                    ]
                ],
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );

            if (this.modelChanged || this.annotationChanged) {
                void leaveConfirmDialog.open().then(() => {
                    leaveConfirmDialog.onRemove(() => {
                        leaveConfirmDialog.waiting.stop();
                        reject(new Error("Navigation rejected"));
                    });
                });
            } else {
                resolve();
            }
        });
    };

    populateAnnotations(annotations: (IAnnotation | INewAnnotation | IIdEntity)[]) {
        const apiUserId = this.api.getUser()?.id;
        const annotationsHash = getClonedAnnotationsForUser(this.app.store, apiUserId);

        const linked = getLinkedExemptionAnnotations(this.model, indexLinkedExemptionAnnotations(annotationsHash), annotations);
        return ([...annotations, ...linked] as (IAnnotation | INewAnnotation | IIdEntity)[]).reduce((ann: string[], annotation) => {
            let id = annotation.hasOwnProperty("id") && annotation["id"];
            let populatedAnnotation: IAnnotation | INewAnnotation | IIdEntity = annotationsHash[id] || annotation;
            populatedAnnotation.hasOwnProperty("comment") && ann.push(formatExemptionEditAnnotation(
                populatedAnnotation["comment"],
                timestamp => this.formatDate(dstCalculator.toLocalDateTime(new Date(timestamp))) || timestamp
            ));
            return ann;
        }, []);
    }

    formatDate(dateToFormat) {
        return dateToFormat ? dayjs(dateToFormat).format("LLL") : null;
    }

    getTrailers() {
        let logDate = this.model.getDate();
        let device = this.model.device;

        if (device && device.id && device.id !== "NoDeviceId") {
            let nextLog = this.app.dutyStatusLogService.getNormalNext(this.logCollection, this.model);
            let logEndDate = nextLog ? nextLog.getDate() : getServerDate();
            return this.trailerAttachmentCollection.searchForTrailers(logDate, logEndDate, device.id);
        }
        return [];
    }

    getLogDuration(model: any): number {
        if (this.exemptionStartLog && this.exemptionEndLog) {
            return (new Date(this.exemptionEndLog.dateTime).getTime() - new Date(this.exemptionStartLog.dateTime).getTime()) / MINUTES;
        }
        return model ? this.app.dutyStatusLogService.getDuration(this.logCollection, model, true) / MINUTES : 0;
    }

    render() {
        // It's possible that there is no model (no id provided or non-existent id provided),
        // and it will try to render anyways (while redirecting) so add this line as to not show an error.
        if (this.needGoBack()) {
            return this;
        }
        const store = this.app.store;
        const pageState = store.getState().mobile.pageState;
        if (pageState?.title === "HOS Log" && store.getState().data.ELDJurisdiction === "EWD") {
            store.dispatch(MobileActions.setPageState({
                ...pageState,
                ...{ title: "Work Diary Log" }
            }));
        }

        let violations: any[] = [];
        let violationSavedValues: any[] = [];
        let realLog: DutyStatusLog = this.logFromStorage || this.model;

        let isStandardLog = (log: DutyStatusLog) => !log.isControlLog() && !log.isPlaceholder();
        let canEditNextLog = (log: DutyStatusLog) => !log.isUneditableLog() && !log.isAutomaticLog() && !log.hasPendingRequest();

        let nextLog = this.app.dutyStatusLogService.getNormalNext(this.logCollection, this.model);
        let newPrevLog = this.app.dutyStatusLogService.getLatestLog(this.logCollection, this.model);
        let previousLog = this.app.dutyStatusLogService.getNormalPrevious(this.logCollection, this.model);
        let isAutomaticLog = realLog.isAutomaticLog();
        let isUneditableLog = realLog.isUneditableLog() || shouldBlockEditingDLog(realLog, this.app.dutyStatusLogService);
        let canEditAndAnnotate = isStandardLog(this.model);
        let isDriverUnassigned = realLog.driver.id !== NOUSERID ? this.model.driver.id === NOUSERID : false;

        let canEditDuration = !realLog.isAutomaticOn() && canEditAndAnnotate && !isUneditableLog
            && nextLog && canEditNextLog(nextLog) && this.getLogDuration(realLog) < 24 * 60
            && !isDriverUnassigned;
        let canEditStartDate = canEditAndAnnotate && !isUneditableLog && !isAutomaticLog
            && !(previousLog && previousLog.isAutomaticDriving()) && !(newPrevLog && newPrevLog.isAutomaticDriving())
            && !isDriverUnassigned;
        let canEditStatus = canEditAndAnnotate && !isUneditableLog && !isDriverUnassigned;

        this.violations.forEach(violation => {
            let violationValues = violation.reason.match(VIOLATION_HOURS_REGEX) || [];
            let violationRawString = violation.reason;
            violationValues.forEach((violationValue, index) => {
                violationRawString = violationRawString.replace(violationValue, `{${ index }}`);
            });
            violations.push(violationRawString);
            violationSavedValues.push(violationValues);
        });

        this.populateModel()
            .then(() => this.app.localizerService.initUserUnits(this.translator))
            .then(() => this.translator.translate(violations))
            .then(violationStrings => {
                let violationObjs = this.violations.map((violationObj, i) => {
                    let violationValues = violationSavedValues[i];
                    let violationTranslatedString = violationStrings[i];
                    violationValues.forEach((violationValue, index) => {
                        violationTranslatedString = violationTranslatedString.replace(`{${ index }}`, violationValue);
                    });

                    let currentDate = getServerDate().toISOString();
                    let fromDate = dayjs(dstCalculator.toLocalDateTime(new Date(violationObj.start > currentDate ? currentDate : violationObj.start))).format("L LT");

                    return _.extend({}, violationObj, { _fromDate: fromDate, reason: violationTranslatedString });
                });
                let modelForRequestedChanges = this.exemptionStartLog && this.requestedExemptionRelatedLog ? this.exemptionStartLog : this.model;
                let requestedChanges = !modelForRequestedChanges.requestedEdit || (modelForRequestedChanges.requestedEdit.state !== "Requested"
                    && modelForRequestedChanges.requestedEdit.state !== "Transitioning") ? null : modelForRequestedChanges.getRequestChanges();
                if (!requestedChanges && this.requestedExemptionRelatedLog?.requestedEdit && this.requestedExemptionRelatedLog?.isSituationalDrivingClear() && this.exemptionStartLog) {
                    requestedChanges = {
                        "duration": convertMinutesToTimeString(
                            (new Date(this.requestedExemptionRelatedLog.requestedEdit.dateTime).getTime() - new Date(this.exemptionStartLog.dateTime).getTime()) / MINUTES
                        )
                    };
                }
                let isRequestedAdd = this.model.state === "Requested" || this.model.state === "Transitioning";
                let logIgnoreToggleSwitch = this.toggleSwitch.create();
                let logAddress = this.model.isValidLocation() ? this.getFormattedAddress(this.model.location) : undefined;
                let renderPage = () => {
                    return this.app.dutyStatusLogService
                        .getAnnotations(this.model, this.translator)
                        .then(anns => {
                            const customExemptHOSAbbreviation = getCustomExemptHOSAbbreviation(this.app.store.getState().systemSettings);
                            return this.display(
                                this.template({
                                    log: this.model,
                                    requestedChanges,
                                    isRequestedAdd,
                                    trailers: this.getTrailers(),
                                    dateTime: this.formatDate(dstCalculator.toLocalDateTime(this.model.getDate())),
                                    createdDate: this.model._createdDate && this.formatDate(dstCalculator.toLocalDateTime(this.model._createdDate)),
                                    duration: convertMinutesToTimeString(this.duration || this.getLogDuration(this.model)),
                                    verifyDateTime: this.formatDate(this.model.getVerifyDate()),
                                    annotations: this.populateAnnotations(anns),
                                    violations: violationObjs,
                                    canEditStartDate,
                                    canEditDuration,
                                    canEditStatus,
                                    requestedExemptionRelatedLog: this.requestedExemptionRelatedLog,
                                    showIgnoreLogToggle: this.canIgnoreLog && !isDriverUnassigned && this.model.isIgnorableLog(),
                                    logIgnoreToggleSwitch,
                                    isControlLog: this.model.isControlLog([customExemptHOSAbbreviation] as THosStatus[]),
                                    controlLogBadge: this.model.getControlLogBadge(),
                                    originDescription: this.model.getOriginDescription(),
                                    odometer: this.isEwd && typeof this.model.odometer !== "undefined" && this.app.localizerService.toLocalDistanceString(this.model.odometer),
                                    errors: this.errors.reduce(
                                        (res, error) => {
                                            res[error.type] = error.text;
                                            return res;
                                        },
                                        <any>{}
                                    ),
                                    dateTimeErrorLog: this.dateTimeErrorLog,
                                    hosAddressHtml: this.hosAddress.getHtmlTemplate({
                                        id: this.model.id,
                                        address: logAddress,
                                        hasAddress: this.model.isValidLocation(),
                                        isRequestedEdit: false
                                    }),
                                    isEditableYmOrPcLog: this.isEditableYmOrPcLog(),
                                    isUneditableDLog: shouldBlockEditingDLog(this.model, this.app.dutyStatusLogService),
                                    _translatedStatus: getTranslatedStatus(this.model.status, customExemptHOSAbbreviation, this.getString.bind(this)),
                                    _translatedRequestedStatus:
                                        requestedChanges &&
                                        requestedChanges["status"]
                                            ? getTranslatedStatus(
                                                  requestedChanges["status"],
                                                  customExemptHOSAbbreviation,
                                                  this.getString.bind(this)
                                              )
                                            : null
                                })
                            );
                        })
                        .then(() => {
                            let annotationContainer = <HTMLInputElement> this.el.querySelector("#addAnnotationForm");
                            if (annotationContainer) {
                                // Read the text now, not the copy taken when render() started: the user may have typed
                                // into the previous input while this render was still in progress.
                                const currentAnnotationText = this.getAnnotationText();
                                this.annotationInput?.remove();
                                this.annotationInput = new AutoCompletionInput({
                                    app: this.app,
                                    el: annotationContainer,
                                    action: this.modelChanged ? "edit" : this.model.status,
                                    maxSize: 60,
                                    value: currentAnnotationText,
                                    placeholder: "Add an annotation...",
                                    onChange: this.onAnnotationSet.bind(this)
                                });
                                this.annotationInput.render();
                            }
                            this.showOrHideIgnoredLogNotice(!!this.model.isIgnored);
                            this.errors.length && this.scrollToFirstError();
                            this.renderAddress(this.model.location);
                            this.renderCoDrivers(this.model.coDrivers);
                            if (this.scrollToBottom) {
                                let saveButton = document.getElementById("saveDutyStatusLog");
                                saveButton && saveButton.scrollIntoView();
                                this.scrollToBottom = false;
                            }

                            this.errors = [];
                        });
                };

                if (this.model.status === "PC_Exempted") {
                    this.model.status = getCustomExemptHOSAbbreviation(this.app.store.getState().systemSettings);
                }

                if (isRequestedAdd || requestedChanges) {
                    canEditAndAnnotate = canEditStartDate = canEditStatus = canEditDuration = false;
                }

                if (requestedChanges) {
                    if (requestedChanges.hasOwnProperty("dateTime")) {
                        requestedChanges["dateTime"] = this.formatDate(dstCalculator.toLocalDateTime(requestedChanges["dateTime"]));
                    }

                    if (requestedChanges.hasOwnProperty("device")) {
                        return this.deviceCache.getOrFetch(this.api, { id: requestedChanges["device"].id }).then(devices => {
                            if (requestedChanges && devices && devices[0] && devices[0][0]) {
                                requestedChanges["device"] = devices[0][0];
                            }
                            return renderPage();
                        }, renderPage);
                    }
                }

                return renderPage();
            })
            .catch(handleApi);

        return this;
    }

    createAnnotation(annotationText: string) {
        return {
            id: idSerializer.get(),
            dateTime: getServerDate(),
            driver: this.currentUser ? { id: this.currentUser.id } : "NoUserId",
            comment: annotationText
        } as IAnnotation;
    }

    editStatus() {
        let statusDialog = new SelectDialog({
            translator: this.translator,
            title: "Choose status",
            validate: (status: string) => {
                let log = this.model.clone();
                let isValid = true;

                if (log.status !== "ON" && status !== log.status && log.isAutomaticLog() && status === "D") {
                    isValid = false;
                }

                !isValid && this.showMessage("Invalid status", "You cannot change an automatic duty status log from non-driving to driving.");

                return isValid;
            },
            selectCallback: statusObj => {
                let realLog = this.logFromStorage || this.model;
                let status = statusObj.id as THosStatus;

                if (realLog) {
                    if (realLog.status === "ON" && (realLog.origin === "Automatic" || realLog.origin === "Unassigned")) {
                        const originalLog = this.app.dutyStatusLogService.getLogById(this.app.dutyStatusLogService.getHOSLogs(false), realLog.id);
                        if (!originalLog) {
                            throw new Error("A log that does not exist cannot be modified.");
                        }
                        if (status !== "ON") {
                            this.model.origin = "Manual";
                        } else {
                            this.model.origin = originalLog.origin;
                        }
                        this.model.driver = originalLog.driver;
                    }

                    if (status !== this.model.status) {
                        this.modelChanged = true;
                        this.model.status = status;
                    }
                }
                this.checkForAnnotation();
                this.render();
            },
            isUserNightMode: this.app.store.getState().data.nightMode
        });

        let formatRenderItems = (status: string) => {
            return {
                id: status,
                name: status,
                isSelected: status === this.model.status
            };
        };

        if (this.isEwd) {
            statusDialog.render(["Rest", "Work"].map(formatRenderItems));
        } else if (isSleeperBerthDisabled(this.app.store, this.model.device)) {
            statusDialog.render(["OFF", "ON", "D"].map(formatRenderItems));
        } else {
            statusDialog.render(["OFF", "SB", "ON", "D"].map(formatRenderItems));
        }
    }

    editDate() {
        const dateTimeDialog = new DateTimeSelect({
            translator: this.translator,
            date: this.isEwd ? roundBasedOnStatus(this.model.getDate(), this.model.status) : this.model.getDate(),
            language: this.app.translationService.getLanguage() as TSupportedLanguage | undefined,
            validate: (localDateTime: Date) => {
                let log = this.model.clone();

                log.dateTime = dstCalculator.toReverseOffsetDateTime(localDateTime).toISOString();
                let isValid = this.validator.validate(
                    log,
                    this.initialCollection,
                    [ValidatorParameters.dateTime, ValidatorParameters.previousLogStatus],
                    this.isEwd ? this.app.dutyStatusLogService.getLogBasedOnDate(log.getDate(), true, this.model.id) : null
                );

                if (log.isAutomaticOn()) {
                    isValid = false;
                }
                !isValid && this.showMessage("Invalid log date", this.validator.errors[0].text);

                return isValid;
            },
            selectCallback: dateTime => {
                if (this.model) {
                    let convertedDateTime = dstCalculator.toReverseOffsetDateTime(dateTime);
                    let convertedDateTimeString = convertedDateTime.toISOString();
                    let roundedLogDate = this.model.dateTime.substring(0, this.model.dateTime.lastIndexOf(":")) + ":00.000Z";
                    if (convertedDateTimeString !== roundedLogDate) {
                        this.model.dateTime = convertedDateTimeString;
                        this.modelChanged = true;
                        this.logCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.logCollection);
                    }
                    this.checkForAnnotation();
                    this.render();
                }
            },
            isUserNightMode: this.app.store.getState().data.nightMode,
            isEwd: this.isEwd
        });

        dateTimeDialog.render();
    }

    editExemption() {
        const currentLog = this.exemptionStartLog as DutyStatusLog;
        const nextLog = this.exemptionEndLog as DutyStatusLog;

        // Determine the initial end time: use next log's time minus 1 minute
        const latestValidEndDateTime = dstCalculator.toLocalDateTime(nextLog.getDate());
        latestValidEndDateTime.setMinutes(latestValidEndDateTime.getMinutes() - 1);

        const validationError = validateExemptionTimeModification(
            latestValidEndDateTime,
            currentLog,
            nextLog
        );

        // Default to Remove if exemption is too short to shorten, otherwise default to Shorten
        const defaultAction = validationError === ShortenYmPcError.ExemptionTooShortToShorten
            ? EditPcYmAction.Remove
            : EditPcYmAction.Shorten;

        const modal = new ShortenOrRemoveLogDialog({
            translator: this.translator,
            title: "Shorten or Remove",
            language: this.currentUser?.language,
            isUserNightMode: this.app.store.getState().data.nightMode,
            latestValidEndDateTime: latestValidEndDateTime,
            currentLog,
            nextLog,
            defaultAction,
            selectCallback: (newSdcDateTime: Date | null, action: EditPcYmAction) => {
                let params: IReviewChangesParams = {
                    exemptionAction: action,
                    logId: this.exemptionStartLog?.id as string
                };
                if (action === EditPcYmAction.Shorten && newSdcDateTime) {
                    params.newSdcDateTime = newSdcDateTime.toISOString();
                }

                this.app.router.navigate(`hos/tabs/logs/logsReviewChanges,${ rison.encode(params) }`, true);
            }
        });
        modal.render();
    }

    editDuration() {
        let startingDuration = this.duration || this.getLogDuration(this.model);
        let realLog = this.model;
        let durationDialog = new DurationSelect({
            translator: this.translator,
            duration: startingDuration,
            validate: duration => {
                if (this.isEwd) {
                    return true;
                }

                let log = this.model.clone();
                let nextLog = this.app.dutyStatusLogService.getNormalNext(this.logCollection, realLog);

                if (log && nextLog) {
                    let logDate: Date = log.getDate();

                    nextLog = nextLog.clone();
                    logDate.setMinutes(logDate.getMinutes() + duration);
                    nextLog.dateTime = logDate.toISOString();

                    let isValid = this.validator.validate(nextLog, this.initialCollection, [ValidatorParameters.dateTime, ValidatorParameters.previousLogStatus]);
                    !isValid && this.showMessage("Invalid log date", this.validator.errors[0].text);
                    return isValid;
                }

                return true;
            },
            resetCallback: !this.isEwd ? undefined : () => {
                this.duration = null;
                this.checkForAnnotation();
                this.render();
            },
            selectCallback: (duration) => {
                if (this.isEwd) {
                    if (duration === startingDuration) {
                        return;
                    }

                    if (duration === 0) {
                        this.handleErrors([new PageError("dateTime", "The Duration cannot be 0.")]);
                        return;
                    }

                    this.duration = duration;
                    this.modelChanged = true;
                    this.checkForAnnotation();
                    this.render();
                    return;
                }

                let log = this.model.clone();

                if (log && this.initialNextLog) {
                    let logDate: Date = log.getDate();
                    logDate.setMinutes(logDate.getMinutes() + duration);
                    this.initialNextLog.dateTime = logDate.toISOString();
                    this.modelChanged = true;
                    this.checkForAnnotation();
                    this.render();
                }
            },
            isUserNightMode: this.app.store.getState().data.nightMode,
            isEwd: this.isEwd
        });

        durationDialog.render();
    }

    isEditableYmOrPcLog() {
        if (!this.canModifyPcYmPeriod || !this.exemptionStartLog || !this.exemptionEndLog) {
            return false;
        }

        const log = this.exemptionStartLog;
        const isFirstLogYmOrPc = log.status === "YM" || log.status === "PC";
        return isFirstLogYmOrPc && this.exemptionEndLog.isSituationalDrivingClear();
    }

    saveLog() {
        this.checkForAnnotation();
        this.save()
            .then((realLog: DutyStatusLog) => {
                toastr.success(this.getString("Edits saved"));
                const syncWindowStart = this.hosUtils.getRetentionStartDate(2);
                // this.model.getDate() == log's potentially new date, this.logFromStorage.getDate() == log's original date
                // necessary to ensure cache is invalidated when log is moved in/out of sync window
                const editedFromHistoricalDay = !!this.logFromStorage && this.logFromStorage.getDate() < syncWindowStart;
                if (this.model.getDate() < syncWindowStart || editedFromHistoricalDay) {
                    this.invalidateHistoricalCache();
                }

                // Mirror the in-window behaviour: editing a log must not move the logs screen to the
                // log's new day. In-window edits stay put because the parent log keeps its original date
                // (almost "intentionally" serving stale data)
                // `!this.cancelRedirect` also called here to prevent potential stale `lastViewedLog`
                // if user exists edit screen before save resolves
                if (editedFromHistoricalDay && !this.fromMain && !this.cancelRedirect) {
                    this.app.store.dispatch(MobileActions.setLastViewedLog({
                        date: getStartOfDayUserTimezone(this.logFromStorage.getDate()).toISOString()
                    }));
                }
                this.goBack(realLog);
            })
            .catch(this.handleErrors);
    }

    goBack(savedLog?: DutyStatusLog) {
        if (this.workflow) {
            dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
        } else {
            if (this.cancelRedirect) {
                return;
            }

            if (!this.fromMain) {
                this.app.router.navigate("hos/tabs/logs/logs", true);
            } else {
                this.app.store.dispatch(MobileActions.setLastViewedLog(undefined));
                this.app.router.navigate("hos", true);
                toastr.info(
                    this.getString("Saved"),
                    undefined,
                    [
                        {
                            linkText: this.getString("View log"),
                            linkId: "back-to-main-toast",
                            linkCallback: () => {
                                !!savedLog &&
                                    this.app.store.dispatch(
                                        MobileActions.setLastViewedLog({
                                            id: savedLog.id,
                                            date: getStartOfDayUserTimezone(new Date(savedLog.dateTime)).toISOString()
                                        })
                                    );
                                this.app.router.navigate("hos/tabs/logs/logs", true);
                            }
                        }
                    ],
                    {
                        positionClass: "toast-bottom-center",
                        timeOut: constants.HOS.TOAST_MESSAGE_TIMEOUT,
                        origin: ToastOrigins.HOS_STATUS_CHANGE,
                        isImportant: false
                    }
                );
            }
        }
    }

    showMessage(title, message) {
        let messageDialog = new Dialog(
            "message-dialog",
            message,
            this.translator,
            title,
            [
                {
                    text: "OK",
                    callback: dialog => {
                        dialog.remove();
                    }
                }
            ],
            {
                closable: true,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void messageDialog.open();
    }

    acceptRequest(e: Event) {
        this.saveRequest(e, true)
            .then(() => {
                // Accepting a requested edit can change the status that continues into later days.
                // Drop the user's cached historical days so the logs view re-fetches fresh boundary
                // continuations instead of serving the stale "requested" value from the cache.
                this.invalidateHistoricalCache();
                toastr.success(this.getString("Edits accepted"));
                this.goBack();
            })
            .catch(error => {
                this.handleErrors(error);
                this.toggleDisableRequestButtons(true, e, this.getString("Accept"));
            });
    }

    rejectRequest(e: Event) {
        this.saveRequest(e, false)
            .then(() => {
                // Rejecting a requested edit on a historical day must clear the cache just like
                // accepting does. Without this, the historicalDayTracker still holds the log with
                // requestedEdit.state = "Requested", which causes two bugs:
                //   1. Reopening the rejected log navigates back immediately (blank page) once any
                //      other operation evicts the stale entry from the cache.
                //   2. "Accept and certify" includes the stale log in getNotVerifiedLogs because
                //      hasPendingRequest() is still true, producing a spurious certification
                //      checkmark in Drive that MyGeotab correctly does not show.
                this.invalidateHistoricalCache();
                toastr.success(this.getString("Edits rejected"));
                this.goBack();
            })
            .catch(error => {
                this.handleErrors(error);
                this.toggleDisableRequestButtons(true, e, this.getString("Reject"));
            });
    }

    unassignLog() {
        let pairedLog = this.app.dutyStatusLogService.findPairedLog(this.model, this.logCollection);

        if (!pairedLog) {
            this.model.driver = { id: NOUSERID };
            this.modelChanged = true;
            return;
        }

        let dialogTemplate = _.template(unassignDialogHtml);
        this.populateLogAddressPromise(pairedLog)
            .then(location => {
                pairedLog = pairedLog as DutyStatusLog;
                pairedLog.location = location as IAddressLookupData;

                let messageDialog = new Dialog(
                    "unassign-message-dialog",
                    dialogTemplate({
                        status: pairedLog.status,
                        dateTime: this.formatDate(dstCalculator.toLocalDateTime(pairedLog.getDate())),
                        duration: convertMinutesToTimeString(this.getLogDuration(pairedLog)),
                        formattedAddress: this.getFormattedAddress(location)
                    }),
                    this.translator,
                    "Unassign paired log as well?",
                    [
                        {
                            text: "Cancel",
                            className: "button_secondary",
                            id: "unassign-cancel",
                            callback: () => messageDialog.remove()
                        },
                        {
                            text: "Confirm",
                            id: "unassign-confirm",
                            callback: () => {
                                this.model.driver = { id: NOUSERID };
                                this.modelChanged = true;
                                messageDialog.remove();
                                this.render();
                            }
                        }
                    ],
                    {
                        closable: true,
                        isNightMode: this.app.store.getState().data.nightMode
                    }
                );
                void messageDialog.open();
            })
            .catch(this.handleErrors);
    }

    remove() {
        this.cancelRedirect = true;
        this.annotationInput && this.annotationInput.remove();
        super.remove();
        return this;
    }

    private readonly handleErrors = (errors = [] as any[]) => {
        let errorsArray = Array.isArray(errors) ? errors : [errors];
        errorsArray.forEach(error => {
            if (error) {
                error instanceof PageError
                    ? this.errors.push(error)
                    : handleJSNative(error instanceof Error ? error : new Error(error));
            }
        });
        this.render();
    };

    private toggleIgnoreLog(): void {
        let isIgnored = !!this.model.isIgnored;
        this.toggleSwitch.toggle(!isIgnored);
        this.model.isIgnored = !isIgnored;
        this.needsConfirmIgnore = !isIgnored !== this.logFromStorage.isIgnored;
        this.modelChanged = true;
        this.checkForAnnotation();
        this.render();
        this.showOrHideIgnoredLogNotice(!isIgnored);
    }

    private showOrHideIgnoredLogNotice(isIgnored: boolean) {
        let ignoredLogNotice = document.getElementById("ignoredLogNoticeBoxLogEdit");
        let toggleTextLabel = document.getElementById("ignoreLog-toggle-label");
        if (!toggleTextLabel || !ignoredLogNotice) {
            return;
        }
        if (isIgnored) {
            show(ignoredLogNotice);
            toggleTextLabel.innerHTML = this.getString("Yes");
        } else {
            hide(ignoredLogNotice);
            toggleTextLabel.innerHTML = this.getString("No");
        }
    }

    private needGoBack() {
        if (!this.model) {
            return true;
        }

        let requestedEdit = this.model.requestedEdit;
        let driverIsCurrentUser = !!this.currentUser && this.model.getDriverId() === this.currentUser.id;
        let requestedDriverId = requestedEdit && requestedEdit.driver && requestedEdit.driver.id;
        let currentDriverId = this.currentUser && this.currentUser.id;
        return !this.model.status || (!driverIsCurrentUser && requestedDriverId && requestedDriverId !== currentDriverId);
    }

    private scrollToFirstError() {
        let firstError: PageError | null = this.errors[0];
        if (firstError) {
            let firstErrorField = <HTMLElement>document.querySelector(`[data-for="${ firstError.type }"]`);
            let content: HTMLElement | null = document.getElementById("scroll-content");
            if (content && firstErrorField) {
                content.scrollTop += firstErrorField.getBoundingClientRect().top - content.getBoundingClientRect().top;
            }
        }
    }

    private viewExistingLog() {
        if (!this.dateTimeErrorLog) {
            return;
        }

        let ruleResult = this.hosUtils.getRuleResult();
        let violations = ruleResult ? ruleResult.violations : [];
        let logId = this.dateTimeErrorLog.id;
        let filteredViolations: IHosViolationSlim[] = violations
            .filter(vio => vio.logId === logId)
            .map((vio: any) => ({ start: new Date(vio.fromDate).toISOString(), reason: populateViolationReason(vio) }));

        this.app.router.navigate(`hos/tabs/logs/view,${ rison.encode({ id: logId, violations: filteredViolations }) }`, true);
    }

    private getAnnotationText() {
        let annotation = this.annotationInput?.val || "";
        return annotation.replace(/[\r\n|\r|\n]+/, " ").trim();
    }

    private isDateEditedSimple(editedLogDate: string) {
        let indexOfSeconds = editedLogDate.lastIndexOf(":");
        return editedLogDate.substring(0, indexOfSeconds) !== this.logFromStorage.dateTime.substring(0, indexOfSeconds);
    }

    private confirmIgnoredLog(newLog: DutyStatusLog) {
        if (!this.currentUser) {
            return Promise.reject();
        }
        let ruleSetId = this.hosUtils.getLatestHosRuleSetIdShortened(true);
        let latestLog = this.hosUtils.getLatestLogForUser(this.currentUser as IIdEntity);
        let hasAvailability = this.hosUtils.hasAvailability(ruleSetId);
        let ruleResult = hasAvailability ? this.hosUtils.getPreviewAvailabilityForNewIgnoredLog(newLog) : undefined;
        let availabilityHtml = "";
        if (hasAvailability && ruleResult) {
            availabilityHtml = this.availabilityDisplay.getDisplay(
                ruleResult,
                ruleSetId,
                latestLog,
                this.getString("{0} days"),
                "HOS",
                undefined,
                false,
                this.getString("h"),
                this.getString("{minutes}m").replace("{minutes}", ""),
                this.getString("{0} Break duration"),
                this.getString("{0} Break"),
                this.getString("{0} Break required in")
            );
        }

        let violationErrors = hasAvailability ? this.hosUtils.checkIfInOrNearViolation(ruleResult, ruleSetId) : [];
        return (violationErrors.length ? Promise.all(violationErrors.map(violationError => violationError.getNoticeBoxMessage(this.translator))) : Promise.resolve([])).then(violationMessages => {
            let violationWarnings = "";
            violationMessages.forEach(violationMessage => {
                violationWarnings += violationMessage;
            });
            let availabilityPreviewTemplate = _.template(availabilityPreviewDialog);

            let dialogTemplate = availabilityPreviewTemplate({
                newRuleSet: undefined,
                isIgnoringLog: newLog.isIgnored,
                availabilityDisplayHtml: availabilityHtml,
                zoneOrCycleChanges: undefined,
                violationWarnings
            });

            let dialog = new Dialog(
                "ignoreLog-dialog",
                `<div id="ignorelog-availability-preview">${ dialogTemplate }</div>`,
                this.translator,
                "Availability Preview",
                [
                    {
                        text: "Back",
                        id: "cancel-ignoreLog",
                        className: "button_secondary",
                        callback: () => {
                            this.annotationChanged = false;
                            this.modelChanged = false;
                            dialog.remove();
                            this.goBack(this.logFromStorage);
                        },
                        spinner: true
                    },
                    {
                        text: "Confirm",
                        id: "verify-ignoreLog",
                        callback: () => {
                            this.needsConfirmIgnore = false;
                            return this.save()
                                .then((realLog: DutyStatusLog) => {
                                    this.goBack(realLog);
                                    dialog.remove();
                                })
                                .catch(error => {
                                    this.handleErrors(error);
                                    dialog.remove();
                                });
                        },
                        spinner: true
                    }
                ],
                {
                    isNightMode: this.app.store.getState().data.nightMode,
                    closable: false
                }
            );
            return dialog.open();
        });
    }

    private save() {
        return new Promise((resolve, reject) => {
            let realLog = this.logFromStorage.clone();
            let editedLog = this.model;
            let editedLogDate = editedLog.getDate();
            let nextLog: any | null = this.app.dutyStatusLogService.getNormalNext(this.logCollection, realLog);
            let annotationText: string = this.getAnnotationText();
            let modelChanged = this.modelChanged;
            let annotationChanged = this.annotationChanged;
            let isDriverUnassigned = realLog.driver.id !== NOUSERID && editedLog.driver.id === NOUSERID;
            let finishWithError = error => {
                // if something goes wrong, undo clearChanges();
                this.modelChanged = modelChanged;
                this.annotationChanged = annotationChanged;
                reject(error);
            };

            if (this.isDateEditedSimple(editedLog.dateTime)) {
                let logWithSameDateTime = this.app.dutyStatusLogService.getLogWithIdenticalDateTime(editedLogDate);
                if (logWithSameDateTime) {
                    this.dateTimeErrorLog = logWithSameDateTime;
                    this.render();
                    return;
                }
            }

            // copy attributes that might have been edited
            realLog.dateTime = editedLog.dateTime;
            realLog.status = editedLog.status;
            realLog.driver = editedLog.driver;
            realLog.isIgnored = editedLog.isIgnored;
            realLog.origin = editedLog.origin;

            let logsToAdd: DutyStatusLog[] = [];
            let logsToEdit: DutyStatusLog[] = [];
            if (this.duration) {
                let logEndDate = new Date(editedLogDate.getTime());
                logEndDate.setMinutes(logEndDate.getMinutes() + this.duration);

                if (this.app.dutyStatusLogService.isInvalidFutureDate(logEndDate, this.app.dutyStatusLogService.getLogBasedOnDate(logEndDate, true, this.model.id))) {
                    this.handleErrors([new PageError("dateTime", "The selected Duration is invalid because it creates a log set in the future.")]);
                    return;
                }

                let logCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs().filter(hosLog => hosLog.isNormalLog()));
                let [newLogs, editedLogs] = logDurationCalculator().getLogsToAddEdit(logCollection, realLog, logEndDate, true);
                logsToAdd = newLogs;
                logsToEdit = editedLogs;
            }
            realLog.addAnnotation(this.createAnnotation(annotationText));

            let allLogs = logsToAdd.concat(logsToEdit);
            if (annotationText) {
                allLogs.map(log => log.addAnnotation(this.createAnnotation(annotationText)));
            }

            this.modelChanged = false;
            this.annotationChanged = false;
            if (this.validator.validate(realLog, this.initialCollection, undefined, this.isEwd ? this.app.dutyStatusLogService.getLogBasedOnDate(realLog.getDate(), true, this.model.id) : null)) {
                this.annotationInput && this.annotationInput.cacheHint(annotationText);
                if (this.needsConfirmIgnore) {
                    this.confirmIgnoredLog(new DutyStatusLog(realLog)).catch(this.handleErrors);
                    return;
                }

                let saveBtn = <HTMLInputElement>document.getElementById("saveDutyStatusLog");
                if (saveBtn) {
                    saveBtn.disabled = true;
                    saveBtn.innerHTML = this.getString("Saving in progress");
                }

                if (!this.isEwd && modelChanged) {
                    realLog.editDateTime = getServerDate().toISOString();
                    logsToEdit.push(...this.app.dutyStatusLogService.getLogsToUnverify(realLog));
                }

                const skipSdc = !realLog.isStandardLog() || realLog.isAutomaticLog();
                let newLogs = this.app.dutyStatusLogService.getAdditionalLogsBeforeNewLogIfDrivingInExemption(new Date(realLog.dateTime), this.currentUser as IUser, skipSdc);
                newLogs.length && logsToAdd.push(...newLogs);

                Promise.all(logsToAdd.map(log => this.sync.dutyStatusLogs.addItem(log, true)))
                    .then(() => Promise.all(logsToEdit.map(log => this.sync.dutyStatusLogs.setItem(log, true))))
                    .then(() => {
                        if (isDriverUnassigned) {
                            let logs = [realLog];
                            let pairedLog = this.app.dutyStatusLogService.findPairedLog(realLog, this.logCollection);
                            if (pairedLog) {
                                pairedLog.driver = { id: NOUSERID };
                                annotationText && pairedLog.addAnnotation(this.createAnnotation(annotationText));
                                logs.push(pairedLog);
                            }

                            return setUsingReassignApi(
                                logs,
                                this.currentUser,
                                true,
                                this.sync
                            );
                        }
                        return this.sync.dutyStatusLogs.setItem(realLog);
                    })
                    .then(() => {
                        // if next log's datetime is unchanged, don't need to unverify it
                        if (this.isEwd || !nextLog || !this.initialNextLog || this.initialNextLog.getDate().valueOf() === nextLog.getDate().valueOf()) {
                            return Promise.resolve();
                        } else {
                            logsToEdit = this.app.dutyStatusLogService.getLogsToUnverify(this.initialNextLog.clone());
                            return Promise.all(logsToEdit.map(log => {
                                if (annotationText && log.id === this.initialNextLog?.id) {
                                    log.addAnnotation(this.createAnnotation(annotationText));
                                }
                                return this.sync.dutyStatusLogs.setItem(log);
                            }));
                        }
                    })
                    .then(() => resolve(realLog))
                    .catch(finishWithError);
            } else {
                finishWithError(this.validator.errors);
            }
        });
    }

    private populateCoDrivers(coDrivers: IIdEntity[]) {
        if (coDrivers.length) {
            return this.userCache
                .getOrFetch(
                    this.api,
                    coDrivers.map(coDriver => ({ id: coDriver.id }))
                )
                .then(populatedCoDrivers => (populatedCoDrivers || []).reduce((acc, items) => acc.concat(items)))
                .catch(() => {
                    // Populate whatever is available
                    return Promise.resolve(
                        coDrivers.map(coDriver => {
                            return this.userCache.get(coDriver)[0] || coDriver;
                        })
                    );
                });
        } else {
            return Promise.resolve([]);
        }
    }

    private readonly populateModel = () => {
        this.fetchCurrentLog(this.model.id);
        let users: any[] = this.app.userService.getUsers(false);
        let requestedEdit = this.model.requestedEdit;
        let driver = this.model.driver;

        if (driver && driver.id !== "NoUserId") {
            this.model.driver = find(users, u => u.id === driver.id);
        }

        if (requestedEdit && requestedEdit.driver) {
            if (requestedEdit.driver.id !== "NoUserId") {
                this.model.requestedEdit = extend(requestedEdit, { driver: find(users, u => requestedEdit && u.id === requestedEdit.driver.id) || requestedEdit.driver });
            }
        }
        let populateAddressPromise = this.populateLogAddressPromise(this.model).then(location => {
            this.model.location = location as IAddressLookupData;
            this.renderAddress(location);
        });

        let populateCodriversPromise = this.populateCoDrivers(this.model.getCoDrivers()).then(coDrivers => {
            this.model.coDrivers = coDrivers;
            this.renderCoDrivers(coDrivers);
        });

        let getDevicesPromise = Promise.resolve();
        let device = this.model.device;
        if (device && device.id !== "NoDeviceId") {
            let devices: IIdDictionary<Device> = this.deviceCache.getDict() as IIdDictionary<Device>;
            if (!devices.hasOwnProperty(device.id)) {
                getDevicesPromise = this.deviceCache
                    .fetch(this.api, { id: device.id }, false)
                    .then(([foundDevice]) => {
                        this.model.device = foundDevice;
                    })
                    // even if we can't get the device name, we should still load the page
                    .catch(() => Promise.resolve());
            } else {
                this.model.device = devices[device.id];
            }
        }

        return Promise.all([populateAddressPromise, populateCodriversPromise, getDevicesPromise]).then(() => {});
    };

    private previewRequestedExemptionChanges() {
        let params: IReviewChangesParams = {
            logId: this.exemptionStartLog?.id as string,
            isRequestedEdit: true
        };
        if (this.requestedExemptionRelatedLog?.isSituationalDrivingClear()) {
            params.newSdcDateTime = this.requestedExemptionRelatedLog.requestedEdit?.dateTime;
        }

        if (this.clickLogAction) {
            this.clickLogAction(params);
        } else {
            this.app.router.navigate(`hos/tabs/logs/logsReviewChanges,${ rison.encode(params) }`, true);
        }
    }

    private populateLogAddressPromise(log: DutyStatusLog) {
        let currentLocation = this.model.location || ({} as IAddressLookupData);
        let coordinate = currentLocation.location;

        if (!coordinate && log.location?.address?.formattedAddress) {
            return Promise.resolve(log.location);
        }

        let populateAddressPromise = Promise.resolve();
        if (coordinate) {
            populateAddressPromise = populateAddressesRedux(this.api, this.app.store, [{
                "id": log.id,
                "coordinate": coordinate
            }], false)
                .then(([address]) => {
                    let location;

                    if (log.isValidLocation() && address && address.formattedHosAddress && address.formattedHosAddress.length) {
                        location = extend(currentLocation, { formattedAddress: address.formattedHosAddress });
                    }

                    return location;
                })
                .then(location => (log.location = location))
                .catch(() => Promise.resolve());
        }
        return populateAddressPromise;
    }

    private renderAddress(location) {
        let el = document.getElementById("logAddress");

        if (el && location) {
            el.textContent = this.getFormattedAddress(location);
        }
    }

    private getFormattedAddress(location) {
        return location.formattedAddress || (location.address ? location.address.formattedAddress : "...");
    }

    private renderCoDrivers(coDrivers) {
        let el = document.getElementById("logCoDrivers");

        if (el && coDrivers) {
            el.textContent = coDrivers
                .map(d => {
                    if (d.firstName || d.lastName) {
                        return `${ d.firstName || "" }${ d.lastName ? ` ${ d.lastName }` : "" }`;
                    }
                    return this.getString("Not available");
                })
                .join(", ");
        }
    }

    private toggleDisableRequestButtons(enable: boolean, e: Event, buttonText: string) {
        const acceptButton = document.getElementById("acceptRequest") as HTMLButtonElement;
        const rejectButton = document.getElementById("rejectRequest") as HTMLButtonElement;
        if (acceptButton && rejectButton) {
            acceptButton.disabled = !enable;
            rejectButton.disabled = !enable;
        }

        if (e && e.currentTarget) {
            (e.currentTarget as HTMLElement).textContent = this.getString(buttonText);
        }
    }

    private invalidateHistoricalCache() {
        // Saving, accepting, or rejecting an edit can change the status that continues into later days, so fully
        // clear the historical day cache; affected days re-fetch fresh on the next visit.
        this.app.store.dispatch(HistoricalDayActions.clearTrackedDays());
    }

    private saveRequest(e: Event, isAccepted: boolean) {
        return new Promise(resolve => {
            if (this.requestedExemptionRelatedLog) {
                return this.savePcYmRequest(e, isAccepted).then(() => resolve());
            }

            this.toggleDisableRequestButtons(false, e, "Saving in progress");

            let pairedLog: DutyStatusLog | null = null;
            let realLog = this.logFromStorage.clone();
            let newlyAddedSDCLogPromise = Promise.resolve();
            if (!realLog.requestedEdit) {
                if (realLog.state !== "Requested") {
                    resolve();
                } else {
                    realLog.state = isAccepted ? "Active" : "Rejected";
                }
            } else {
                pairedLog = this.app.dutyStatusLogService.findPairedLog(realLog, this.logCollection);
                realLog.requestedEdit.coDrivers = realLog.coDrivers;
                setCoDriversForCarrierDriverRequest(isAccepted, realLog.requestedEdit as IDutyStatusLog, realLog as IDutyStatusLog);
                this.app.dutyStatusLogService.saveRequest(realLog, isAccepted);
            }

            if (isAccepted) {
                const skipSdc = !realLog.isStandardLog() || realLog.isAutomaticLog();
                const newLogs = this.app.dutyStatusLogService.getAdditionalLogsBeforeNewLogIfDrivingInExemption(new Date(realLog.dateTime), this.currentUser as IUser, skipSdc);
                if (newLogs.length) {
                    newlyAddedSDCLogPromise = Promise.all(newLogs.map(log => this.sync.dutyStatusLogs.addItem(log, true)));
                }
            }

            if (pairedLog && pairedLog.requestedEdit) {
                pairedLog.requestedEdit.coDrivers = pairedLog.coDrivers;
                setCoDriversForCarrierDriverRequest(isAccepted, pairedLog.requestedEdit as IDutyStatusLog, pairedLog as IDutyStatusLog);
                this.app.dutyStatusLogService.saveRequest(pairedLog, isAccepted);
                return this.sync.dutyStatusLogs
                    .setItem(pairedLog, true, false)
                    .then(() => newlyAddedSDCLogPromise)
                    .then(() => {
                        resolve(this.sync.dutyStatusLogs.setItem(realLog, false, false));
                    });
            } else {
                return newlyAddedSDCLogPromise.then(() => resolve(this.sync.dutyStatusLogs.setItem(realLog, false, false)));
            }
        });
    }

    private savePcYmRequest(e: Event, isAccepted: boolean): Promise<void> {
        return new Promise(resolve => {
            const editGroups = this.exemptionStartLog ? this.app.dutyStatusLogService.getGroupedExemptionLogEdits(undefined, [this.exemptionStartLog.id], isAccepted) : [];

            // Should get 1 group (related to the log on this page)
            const editGroup = editGroups[0];
            if (editGroup && editGroup.editedExemptionLog) {
                this.toggleDisableRequestButtons(false, e, "Saving in progress");

                this.app.dutyStatusLogService.saveRequest(editGroup.editedExemptionLog, isAccepted);
                const promisesToResolve: Promise<void>[] = [this.app.sync.dutyStatusLogs.setItem(editGroup.editedExemptionLog, true, false)];

                if (editGroup.logsToEdit) {
                    editGroup.logsToEdit.forEach(log => {
                        this.app.dutyStatusLogService.saveRequest(log, isAccepted);
                        promisesToResolve.push(this.app.sync.dutyStatusLogs.setItem(log, true, false));
                    });
                }

                if (editGroup.logsToRemove) {
                    editGroup.logsToRemove.forEach(log => {
                        promisesToResolve.push(this.app.sync.dutyStatusLogs.removeItem(log, true));
                    });
                }

                return Promise.all(promisesToResolve)
                    .then(() => this.workflow ? Promise.resolve() : this.app.sync.synchronize(false, null, [SyncType.HOS]))
                    .then(() => resolve());
            }

            resolve();
            return;
        });
    }

    // checkForAnnotation: Sends an error if the model has changed and if the annotation does not exist
    // Purpose: This method is used instead of validateAnnotations as error.type is not set in validateAnnotation
    // and is only done so in validator.ts (validate)
    // Error type is important as scrollToFirstError checks for a specific type before displaying it in render()
    private checkForAnnotation() {
        if (this.modelChanged && !this.getAnnotationText()) {
            this.errors.push(new PageError("annotations", "Add an annotation to this log."));
        } else {
            this.errors = this.errors.filter(error => error.type !== "annotations");
        }
    }

    private readonly onAnnotationSet = (val: string) => {
        this.annotationChanged = !!val;
    };

    private manualInputAddress() {
        this.hosAddress.handleAddressButtonClick(
            this.model,
            this.currentUser,
            this.isEwd,
            this.app.store.getState().data.nightMode,
            this.populateModel,
            () => {}
        );
    }

    private fetchCurrentLog(logId: string) {
        if (this.modelChanged) {
            return;
        }
        let isEWD = this.app.store.getState().data.ELDJurisdiction === "EWD";
        this.logCollection = this.app.dutyStatusLogService.getHOSLogs(true)
            .filter(hosLog => hosLog.isMainLogPageLog(this.currentUser, false, isEWD, this.isEmergencyResponseExemptionEnabled));
        this.logFromStorage = this.app.dutyStatusLogService.getLogById(this.logCollection, logId) as DutyStatusLog;

        // Historical days (outside the sync window) are not in the main store — they only live in
        // the historical day cache. Fall back to it so a requested edit on a historical day can be
        // opened, reviewed and accepted/rejected instead of rendering a blank detail page.
        if (!this.logFromStorage) {
            // Only pull in the target log's own cached day — concatenating every cached day would
            // add duplicate/unrelated logs that corrupt duration, pairing and neighbour lookups.
            const dayLogs = this.getCachedHistoricalDayLogs(logId, isEWD);
            const historicalLog = this.app.dutyStatusLogService.getLogById(dayLogs, logId) as DutyStatusLog;
            if (historicalLog) {
                // Keep the collection chronologically ordered — neighbour/pairing logic
                // (getNormalNext/Previous, findPairedLog, isLastLogOfDay) relies on it.
                this.logCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.logCollection.concat(dayLogs));
                this.logFromStorage = historicalLog;
            }
        }

        this.model = this.logFromStorage && this.logFromStorage.clone();
    }

    private getCachedHistoricalDayLogs(logId: string, isEWD: boolean): DutyStatusLog[] {
        if (!this.api.getUser()?.id) {
            return [];
        }
        const trackedDays = this.app.dutyStatusLogService.getTrackedHistoricalDays();
        const dayKey = Object.keys(trackedDays).find(key =>
            trackedDays[key].hosLogs.some(l => l.id === logId) || trackedDays[key].systemLogs.some(l => l.id === logId));
        if (!dayKey) {
            return [];
        }
        const dayEntry = trackedDays[dayKey];
        return [...dayEntry.hosLogs, ...dayEntry.systemLogs]
            .filter(log => log.isMainLogPageLog(this.currentUser, false, isEWD, this.isEmergencyResponseExemptionEnabled));
    }
}
